import { AxiosResponse } from 'axios'
import BaseTableCard from 'common/components/BaseTableCard'
import BasicTable from 'common/components/BasicTable'
import { DateRange } from 'common/components/DateRangePicker'
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { FeatureKey } from 'common/constants/permission'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { fNumber, f3Number } from 'common/utils/formatNumber'
import { dashboardDateDisplayValue } from 'modules/food/utils'
import { useMutateDeleteGrowthShrimp } from 'modules/growth/api/mutation'
import { useGetGrowthShrimpLineChart } from 'modules/growth/api/queries'
import { useGrowthShrimps } from 'modules/growth/api/selector'
import {
  selectedGrowthShrimpIdState,
  growthShrimpDateTableFilterState,
  growthShrimpStartDateTableFilterState,
  growthShrimpEndDateTableFilterState,
} from 'modules/growth/stores/atom'
import { useGetPondNameById, useSpeciesName } from 'modules/pond/api/selector'
import MoreAction from 'modules/pond/components/MoreAction'
import { SpeciesType } from 'modules/pond/constants'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useRecoilState } from 'recoil'

import { Box, useTheme } from '@mui/material'

import GrowthShrimpDialog from '../GrowthShrimpDialog'
import './style.css'
import { normalizeSpeciesName } from 'utils/analytics'

const GrowthShrimpTable = () => {
  const { t } = useTranslation('growth')

  const { data, isFetching, refetch, fetchNextPage, hasNextPage } = useGrowthShrimps()
  const { refetch: refetchChart } = useGetGrowthShrimpLineChart()
  const { pondId } = useParams()
  const { data: pond } = useGetPondNameById(pondId ?? '')
  const speciesName = useSpeciesName()
  const theme = useTheme()

  const [growthId, setEditId] = useRecoilState(selectedGrowthShrimpIdState)

  const [open, setOpen] = useState(false)
  const deleteGrowthShrimpMutation = useMutateDeleteGrowthShrimp()
  const { trackEvent } = useAnalytics()
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  })

  const onSuccess = (res: AxiosResponse<any>) => {
    const { status } = res.data
    if (status === 200) {
      setTimeout(() => {
        refetch()
        refetchChart()
      }, 1000)
      const normalizedSpecies = normalizeSpeciesName(speciesName)
      trackEvent(
        AnalyticsEvents.growthRecordDeleted,
        normalizedSpecies ? { species: normalizedSpecies } : undefined
      )
    }
  }

  const { dialog, openDialog: openConfirmDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      await deleteGrowthShrimpMutation.mutateAsync(Number(growthId), { onSuccess })
    },
  })

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpenEdit = (id: number) => {
    setEditId(id)
    setOpen(true)
  }

  const onLoadMoreRows = () => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }

  const columns = [
    {
      id: 'date',
      title: t('date'),
      format: formatThaiShortDateTwoDigitYear,
      width: '180px',
    },
    {
      id: 'shrimpAge',
      title: <Trans i18nKey={'pond:animal.age'} values={{ species: speciesName }} />,
      width: '100px',
    },
    {
      id: 'shrimpSize',
      title: pond?.species.id === SpeciesType.Fish ? t('gram.per.one') : t('one.per.kg'),
      width: '150px',
      format: fNumber,
    },
    {
      id: 'growthPDG',
      title: t('growth.PDG'),
      width: '150px',
      format: f3Number,
    },
    {
      id: 'growthId',
      title: '',
      width: '100px',
    },
  ]

  const handleOpenDeleteConfirm = (growthId: number) => {
    setEditId(growthId)
    openConfirmDialog()
  }

  const createTableRows = (
    date: string,
    shrimpAge: number,
    shrimpSize: number,
    growthPDG: number,
    growthId: number
  ) => ({
    date,
    shrimpAge,
    shrimpSize,
    growthPDG,
    growthId: (
      <MoreAction
        featureKey={FeatureKey.Growth}
        isHideOnCompleted={false}
        onClickEdit={() => handleOpenEdit(growthId)}
        onClickDelete={() => handleOpenDeleteConfirm(growthId)}
      />
    ),
  })

  const filteredGrowth = data?.filter((item) => {
    if (!dateRange.startDate || !dateRange.endDate) return true

    const d = new Date(item.date)
    return d >= dateRange.startDate && d <= dateRange.endDate
  })

  const rows = filteredGrowth.map((el) =>
    createTableRows(el.date, el.shrimpAge, el.shrimpCountPerKg, el.PDG, el.id)
  )

  const onStyleCell = (row: any) => {
    return {
      borderBottom:
        row.mealIndex === row.mealLen - 1 ? `1px solid ${theme.palette.grey[300]}` : 'none',
    }
  }

  return (
    <>
      {!isFetching && (
        <Box>
          {dialog}

          <BaseTableCard
            disableRefresh={true}
            title={t('growth.summary')}
            refresh={refetch}
            isLoading={isFetching}
            isEmpty={data.length === 0}
            isDateRangePicker={true}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            displayValue={dashboardDateDisplayValue}
          >
            <Box className="setting-table">
              <BasicTable
                columns={columns}
                onLoading={onLoadMoreRows}
                isLoading={isFetching}
                rows={rows}
                onStyleCell={onStyleCell}
              />
            </Box>
          </BaseTableCard>
          <GrowthShrimpDialog onClose={handleClose} open={open} isEdit />
        </Box>
      )}
    </>
  )
}

export default GrowthShrimpTable
